import { Layers, Truck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { Card, CardContent } from "@/components/ui/card"
import type { LotDetail, LotSourceLoad } from "@/features/lots/api/types"
import { paths } from "@/routes/paths"

interface LotCompositionCardProps {
  lot: LotDetail
}

// Renders the per-load provenance for a lot — one stacked row per
// `ExporterLotLoads` entry. Identifiers are prefixed (Load / Delivery /
// Cooperative) so a fresh reader knows what each number means.
export function LotCompositionCard({ lot }: LotCompositionCardProps) {
  const { t } = useTranslation("lots")
  const sources = (lot.sources ?? []).slice().sort((a, b) => b.bags - a.bags)
  const totalBags = sources.reduce((acc, s) => acc + s.bags, 0)

  return (
    <Card className="p-0">
      {/*<CardHeader>
        <header className="flex items-baseline justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
              {t("detail.composition.eyebrow")}
            </p>
            <h2 className="text-lg font-semibold tracking-tight">
              {t("detail.composition.title")}
            </h2>
          </div>
          <div className="text-[11px] text-muted-foreground tabular-nums">
            {t("detail.composition.summary", {
              loads: sources.length,
              bags: totalBags,
            })}
          </div>
        </header>
      </CardHeader>*/}
      <CardContent className="p-0">
        {sources.length === 0 ? (
          <div className="rounded-md border border-dashed py-6 text-center text-xs text-muted-foreground">
            {t("detail.composition.empty")}
          </div>
        ) : (
          <ul className="divide-y rounded-md border">
            {sources.map((source) => (
              <CompositionRow
                key={`${source.loadId}-${source.bags}`}
                source={source}
                totalBags={totalBags}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function CompositionRow({
  source,
  totalBags,
}: {
  source: LotSourceLoad
  totalBags: number
}) {
  const { t } = useTranslation("lots")
  const navigate = useNavigate()
  const load = source.load
  const cooperativeName = load?.order?.cooperative?.name ?? null
  const deliveryNumber = load?.deliveryForm?.billOfLadingNumber ?? null
  const sharePct =
    totalBags > 0 ? Math.round((source.bags / totalBags) * 100) : 0
  return (
    <li>
      <button
        type="button"
        onClick={() => navigate(paths.receptionDetail(source.loadId))}
        title={t("detail.composition.openLoad")}
        className="flex w-full flex-col gap-2 px-4 py-3 text-left transition-colors hover:bg-muted/40 sm:flex-row sm:items-center sm:gap-6"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
              {t("detail.composition.loadLabel")}
            </span>
            <span className="font-mono text-sm font-medium">
              {load?.loadNumber ?? `#${source.loadId}`}
            </span>
            {load?.plateNumber ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <Truck className="size-3" />
                {load.plateNumber}
              </span>
            ) : null}
          </div>

          <div className="grid gap-1 text-[11px] sm:grid-cols-2">
            <FieldLine
              label={t("detail.composition.cooperativeLabel")}
              value={cooperativeName}
            />
            <FieldLine
              label={t("detail.composition.deliveryLabel")}
              value={deliveryNumber}
              mono
            />
          </div>
        </div>

        <div className="flex items-baseline gap-3 sm:flex-col sm:items-end sm:gap-0.5">
          <div className="flex items-baseline gap-1">
            <span className="text-base leading-none font-semibold tabular-nums">
              {source.bags}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {t("detail.composition.bagsUnit")}
            </span>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground tabular-nums">
            <Layers className="size-3" />
            {t("detail.composition.share", { pct: sharePct })}
          </span>
        </div>
      </button>
    </li>
  )
}

function FieldLine({
  label,
  value,
  mono,
}: {
  label: string
  value: string | null
  mono?: boolean
}) {
  return (
    <div className="min-w-0">
      <span className="mr-1.5 text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </span>
      {value ? (
        <span
          className={
            mono
              ? "truncate align-baseline font-mono"
              : "truncate align-baseline"
          }
        >
          {value}
        </span>
      ) : (
        <span className="text-muted-foreground/60">—</span>
      )}
    </div>
  )
}
